/**
 * CatalogProvider: single source of truth for marketplace catalog data.
 *
 * Owns TWO independent fetches, exposes plugins, loading/error state, and catalog
 * utilities (findByInstallKey, parentIndex, removePlugin) via context. Must be
 * rendered inside AuthGuard so the fetches are gated by authentication.
 *
 * The surprising property, and the point of the design: the second fetch
 * (adoption.json, Contract 7) deliberately sits OUTSIDE isLoading and error.
 * fetchAdoption resolves to null rather than rejecting, so adoption can neither
 * delay nor fail a catalog load. `plugins` is the read-time join of the two, and
 * settles twice per load once a real artifact exists — see mergeAdoption on why
 * array identity is preserved when nothing matched.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AdoptionMeta, MarketplaceEntry, MarketplaceClientConfig } from "../types/index";
import type { AdoptionCatalog } from "@skills-marketplace/contracts/contract-7-adoption";
import { adoptionUrlFor, createMarketplaceClient, fetchAdoption } from "./marketplace-client";
import { buildParentIndex } from "./parent-plugin";
import { reportError } from "./newrelic";

// ── Install-key helpers ────────────────────────────────────────────────────────
// Moved from App.tsx so the provider and all consumers share one definition.

export function installKeyForPlugin(plugin: MarketplaceEntry): string {
  return (
    plugin.installName ??
    `${plugin.source}--${plugin.orgOrOwner.toLowerCase()}--${plugin.repoName.toLowerCase()}`
  );
}

export function findPluginByInstallKey(
  catalog: MarketplaceEntry[],
  installKey: string,
): MarketplaceEntry | null {
  return (
    catalog.find(
      (p) => installKeyForPlugin(p).toLowerCase() === installKey.toLowerCase(),
    ) ?? null
  );
}

// ── Adoption join ──────────────────────────────────────────────────────────────

/**
 * Attach adoption figures to catalog entries by direct `installName` lookup.
 *
 * The artifact is keyed by installName, which the catalog builder guarantees to
 * be unique per entry (`dedupeByInstallName`). So this reconstructs NO key:
 * neither `installKeyForPlugin`'s source/org/repo fallback nor
 * `officialIdentityKey()` distinguishes synthesized per-skill entries — they all
 * collapse onto their parent plugin's identity, which would smear one plugin's
 * figure across every one of its skills. An entry with no installName is simply
 * unmeasurable.
 *
 * Pure and total: an entry with no matching figure is returned untouched (no
 * `adoption` key at all), which is the single empty state — there is no
 * "measured zero". See contracts/contract-7-adoption.ts.
 */
export function mergeAdoption(
  plugins: MarketplaceEntry[],
  adoption: AdoptionCatalog | null,
): MarketplaceEntry[] {
  if (!adoption) return plugins;

  let matched = 0;
  const merged = plugins.map((entry) => {
    // hasOwnProperty, not a bare index read: entries is null-prototype when it comes
    // from validateAdoption, but this function is exported and typed against a plain
    // AdoptionCatalog, so it must not depend on its caller for that. installNames are
    // free-form slugs and one can legitimately be "constructor" or "toString".
    const figure =
      entry.installName && Object.prototype.hasOwnProperty.call(adoption.entries, entry.installName)
        ? adoption.entries[entry.installName]
        : undefined;
    if (!figure) return entry;
    matched++;
    return { ...entry, adoption: figure };
  });

  // Preserving array identity keeps downstream memos from rebuilding over a
  // 1500-entry catalog for no reason — search.ts caches its MiniSearch index in a
  // WeakMap keyed on this array reference.
  //
  // Note this guard is all-or-nothing across the whole array, not per-entry, and
  // `adoption === null` already returned above — so with ~42% coverage it does NOT
  // fire on the happy path. It fires when a non-null artifact overlaps nothing: a
  // preview pointed at another environment's artifact, or a stale key scheme.
  return matched > 0 ? merged : plugins;
}

// ── Context ────────────────────────────────────────────────────────────────────

interface CatalogContextValue {
  plugins: MarketplaceEntry[];
  isLoading: boolean;
  error: string | null;
  /**
   * Force a cache-busting re-fetch. Pass `{ silent: true }` for a best-effort
   * background refetch (e.g. the post-publish "View in Catalog" refresh) that must
   * NOT flip the shared `error` state on failure; the caller has its own fallback
   * UI, and poisoning the global error would wrongly claim the catalog is down
   * (and blank the catalog page) after an otherwise-successful action. The failure
   * is still reported to New Relic. Default (non-silent) sets `error` on failure,
   * for user-initiated retries that want the error surfaced.
   *
   * Resolves to whether the refetch succeeded, so a silent caller (e.g. the manual
   * "Refresh" button in the "processing" detail state) can show scoped feedback on
   * failure without touching the shared `error`.
   */
  refresh: (opts?: { silent?: boolean }) => Promise<boolean>;
  /** Optimistically remove a plugin by install key (used after a successful delete). */
  removePlugin: (installKey: string) => void;
  /** Look up a plugin by its install key. Returns null when not found. */
  findByInstallKey: (installKey: string) => MarketplaceEntry | null;
  /** Inverted skill→parent index built from the catalog. Key = child installName. */
  parentIndex: Map<string, MarketplaceEntry>;
  /** Envelope of the joined adoption artifact ("as of" date, window). Null when unavailable. */
  adoptionMeta: AdoptionMeta | null;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function CatalogProvider({
  children,
  config,
}: {
  children: ReactNode;
  /** Marketplace client config. Pass a stable (memoized) object;
   *  changing it rebuilds the client and re-fetches the catalog. */
  config: Partial<MarketplaceClientConfig>;
}) {
  const [rawPlugins, setRawPlugins] = useState<MarketplaceEntry[]>([]);
  const [adoption, setAdoption] = useState<AdoptionCatalog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => createMarketplaceClient(config), [config]);

  // Adoption lives in a separate artifact next to marketplace.json. Resolved from
  // the same config so a caller overriding catalogUrl (previews, tests) gets the
  // matching sibling.
  const adoptionConfig = useMemo(
    () => ({
      // adoptionUrlDefault, not adoptionUrl: this is a derivation, so it must not
      // outrank an explicitly-set REACT_APP_ADOPTION_URL.
      adoptionUrlDefault: config.catalogUrl ? adoptionUrlFor(config.catalogUrl) : undefined,
      getToken: config.getToken,
    }),
    [config.catalogUrl, config.getToken],
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    client
      .getCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setRawPlugins(catalog.plugins);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        reportError(err, "catalog-fetch-routing");
        setError(err instanceof Error ? err.message : "Failed to load catalog");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client]);

  // Adoption is fetched independently of the catalog, never awaited alongside it:
  // a slow or missing artifact must not delay — or fail — the catalog render. It
  // is also deliberately outside isLoading/error, since fetchAdoption resolves to
  // null instead of rejecting.
  useEffect(() => {
    let cancelled = false;

    void fetchAdoption(adoptionConfig).then((result) => {
      if (!cancelled) setAdoption(result);
    });

    return () => {
      cancelled = true;
    };
  }, [adoptionConfig]);

  const refresh = useCallback(async (opts?: { silent?: boolean }): Promise<boolean> => {
    const silent = opts?.silent ?? false;
    setIsLoading(true);
    if (!silent) setError(null);
    // Kick off the adoption re-fetch first so it overlaps the catalog fetch; it
    // resolves to null on failure, so there is nothing here to catch.
    const adoptionPromise = fetchAdoption(adoptionConfig);
    let ok: boolean;
    try {
      const catalog = await client.refreshCatalog();
      setRawPlugins(catalog.plugins);
      // A successful fetch means the catalog is healthy: clear any stale error. The
      // silent path skips the optimistic clear above, so without this a prior error
      // (e.g. a failed initial load) would survive a good refetch and mask the
      // caller's fallback UI (the "processing" detail state) with "catalog is down".
      setError(null);
      ok = true;
    } catch (err: unknown) {
      reportError(err, "catalog-refresh");
      // A silent refetch must not flip the shared error state; the caller's own
      // fallback UI (e.g. the "processing" detail state) stays in place, and the
      // catalog page isn't blanked over a best-effort background fetch. The boolean
      // return lets that caller show its own scoped failure feedback instead.
      if (!silent) setError(err instanceof Error ? err.message : "Failed to refresh catalog");
      ok = false;
    } finally {
      setIsLoading(false);
    }
    // A refresh must not DEMOTE good adoption data. fetchAdoption collapses every
    // failure into null, so a transient blip would otherwise replace real figures
    // with the empty state — which reads as "no usage yet", an indistinguishable
    // wrong answer rather than a visible error. The mount effect above still sets
    // null, because there it genuinely is the first read.
    const nextAdoption = await adoptionPromise;
    if (nextAdoption) setAdoption(nextAdoption);
    return ok;
  }, [client, adoptionConfig]);

  const removePlugin = useCallback((installKey: string) => {
    setRawPlugins((prev) =>
      prev.filter((p) => installKeyForPlugin(p).toLowerCase() !== installKey.toLowerCase()),
    );
  }, []);

  // The read-time join. Everything downstream consumes the merged list, so no
  // consumer needs to know adoption arrived on a separate request.
  const plugins = useMemo(() => mergeAdoption(rawPlugins, adoption), [rawPlugins, adoption]);

  const findByInstallKey = useCallback(
    (installKey: string) => findPluginByInstallKey(plugins, installKey),
    [plugins],
  );

  const parentIndex = useMemo(() => buildParentIndex(plugins), [plugins]);

  const adoptionMeta = useMemo<AdoptionMeta | null>(() => {
    if (!adoption) return null;
    const { entries: _entries, ...meta } = adoption;
    return meta;
  }, [adoption]);

  const value = useMemo<CatalogContextValue>(
    () => ({ plugins, isLoading, error, refresh, removePlugin, findByInstallKey, parentIndex, adoptionMeta }),
    [plugins, isLoading, error, refresh, removePlugin, findByInstallKey, parentIndex, adoptionMeta],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useCatalog(): CatalogContextValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }
  return ctx;
}
